import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Button, Spinner } from 'react-bootstrap'
import { MenuItem, Table, TableBody, TableCell, TableRow, TextField, Tooltip, Zoom, FormControlLabel, Checkbox, InputAdornment } from '@material-ui/core';
import { formatCurrency } from '../../../utils/formatCurrency';
import api from "../../../services/Api";

import { Budget } from '../../../types/Budget'
import { ListProducts } from './List-Products'

import {
    HeadDataBaseProps,
    BodyDataBaseProps,
    ListWithModalChangeSituation,
    LoadDataParams
} from '../../../components/ListWithModalChangeSituation';

import { useSelector } from 'react-redux';
import { PDFDownloadLink } from '@react-pdf/renderer';
import ReportExcelService from '../../../services/ReportExcelService';
import { ReportDataCell, ReportDataRow, ReportTotals } from '../../../types/ReportData';
import { getFileNameWithDate } from '../../../utils/getFIleNameWithDate';
import PdfReportDocument from '../../../components/Pdf/Report/PdfReportDocument';
import CustomerService from '../../../services/CustomerService';
import useBackendLoad from '../../../hooks/backendReload';
import SellerService from '../../../services/SellerService';
import ApiResourceSelect from '../../../components/ApiResourceSelect';
import { Customer } from '../../../types/Customer';
import { Seller } from '../../../types/Seller';
import { Link } from 'react-router-dom';
import { ReportDetails } from '../../../components/ReportDetails';
import { MainPageContentWrapper } from '../../../components/MainPageContentWrapper';

type ReportBudgetResponse = {
    rows: Budget[];
    count: number;
}

type Filters = {
    status: string;
    customerId: number;
    sellerId: number;
    dateInitial: string;
    dateEnd: string;
}

const headData: HeadDataBaseProps[] = [
    { reference: "id", value: "N°" },
    { reference: "budgetNumber", value: "Nº Orçamento" },
    { reference: "clientName", value: "Cliente" },
    { reference: "sellerName", value: "Vendedor" },
    { reference: "status", value: "Situação" },
    { reference: "budgetDate", value: "Data" },
    { reference: "totalValue", value: "Valor Total" },
];

const productHeadData: HeadDataBaseProps[] = [
    { reference: "code", value: "Cod" },
    { reference: "name", value: "Nome" },
    { reference: "saleValue", value: "Valor" },
    { reference: "type", value: "Tipo" },
];

export function ListReportBudget() {
    const [budgets, setBudgets] = useState<Budget[]>([]);
    const [countBudgetsPaginated, setCountBudgetsPaginated] = useState(0);
    const [countBudgets, setCountBudgets] = useState(0);
    const [bodyData, setBodyData] = useState<BodyDataBaseProps[][]>([]);
    const [isSearching, setIsSearching] = useState(false);

    const [lastSortReference, setLastSortReference] = useState('id');
    const [lastSortDirection, setLastSortDirection] = useState<'ASC' | 'DESC'>('DESC');
    const [customerId, setCostumerId] = useState(0)
    const [dateInitial, setDateInitial] = useState('')
    const [dateEnd, setDateEnd] = useState('')
    const [status, setStatus] = useState('')
    const [sellerId, setSellerId] = useState(0)
    const [showProducts, setShowProducts] = useState(false)
    const filtersRef = useRef<Filters | null>(null);
    const {triggerLoad, setTriggerLoad, reloadData} = useBackendLoad();

    //EXPORT
    const [exportBodyData, setExportBodyData] = useState<ReportDataRow[]>([]);
    const [exportTotals, setExportTotals] = useState<ReportTotals[]>([]);

    const { user } = useSelector((state: any) => state.auth);

    const loadExportData = useCallback(async (
        sortDirection,
        sortReference,
        showProducts,
    ) => {
        const { data } = await api.get<ReportBudgetResponse>("/report/budget", {
            params: {
                filters: filtersRef.current ? JSON.stringify(filtersRef.current) : undefined,
                sortReference,
                sortDirection,
            }
        });

        const {rows, count} = data;

        const exportDataList: ReportDataRow[] = [];

        rows.forEach((reportRow) => {
            let exportProductData: ReportDataRow[] = [];
            const data = mountBodyDataRow(reportRow);
            const products = JSON.parse(reportRow.products)

            data.push(
                {
                    for: "products/services",
                    value: '',
                    secondaryRow: true,
                    jsx: <ListProducts products={products}/>
                },
            );

            const exportCells: ReportDataCell[] = data.map((cell) => ({ id: cell.id, for: cell.for, content: cell.value }));

            if (showProducts) {
                exportProductData = products.map((product: any) => {
                    return {
                        cells: [
                            {for: 'code', content: product.INSTANCE.code ?? ''},
                            {for: 'name', content: product.INSTANCE.name ?? ''},
                            {for: 'saleValue', content: formatCurrency(product.INSTANCE.saleValue) ?? ''},
                            {for: 'type', content: product.INSTANCE.type == 'product' ? 'Produto' : 'Serviço'},
                        ],
                    };
                });
            }

            exportDataList.push({
                cells: exportCells,
                subRow: {
                    headData: productHeadData,
                    bodyData: exportProductData,
                }
            });

            setExportTotals([
                { name: 'Total de orçamentos', value: count },
            ])
        });

        setExportBodyData(exportDataList);
        setCountBudgets(count);
    }, []);

    const loadData = useCallback(async ({
        rowsPerPage,
        currentPage,
        sortDirection,
        sortReference,
    }: LoadDataParams) => {
        const { data } = await api.get<ReportBudgetResponse>("/report/budget", {
            params: {
                skip: rowsPerPage * currentPage,
                take: rowsPerPage,
                filters: filtersRef.current ? JSON.stringify(filtersRef.current) : undefined,
                sortReference,
                sortDirection,
            }
        });

        const {rows, count} = data;

        setBudgets(rows);
        setCountBudgetsPaginated(count);
        setLastSortReference(sortReference);
        setLastSortDirection(sortDirection);
    }, []);

    useEffect(() => {
        const list: BodyDataBaseProps[][] = [];
        const aux = budgets;

        aux.forEach((budget) => {
            const data = mountBodyDataRow(budget);
            const products = JSON.parse(budget.products)

            if (showProducts == true) {
                data.push(
                    {
                        for: "products/services",
                        value: '',
                        secondaryRow: true,
                        jsx: <ListProducts products={products}/>
                    },
                );
            }

            list.push(data);
        });

        setBodyData(list);
    }, [budgets]);

    useEffect(() => {
        loadExportData(lastSortDirection, lastSortReference, showProducts);
    }, [lastSortDirection, lastSortReference, showProducts]);

    function mountBodyDataRow(reportRow: Budget) {
        let data: BodyDataBaseProps[] = [
            {
                for: "id",
                value: String(reportRow.id),
                jsx: <Link to={`/orcamentos/${reportRow.id}`}>{reportRow.id}</Link>,
                id: true
            },
            { for: "budgetNumber", value: reportRow.budgetNumber },
            { for: "clientName", value: CustomerService.getCustomerName(reportRow.customerEntity, reportRow.customerName) },
            { for: "sellerName", value: SellerService.getName(reportRow.sellerEntity) },
            { for: "status", value: reportRow.status == 'open' ? 'Em aberto' : 'Lançado em Pedidos' },
            { for: "budgetDate", value: String(reportRow.budgetDate.split("-").reverse().join("/")) },
            { for: "totalValue", value: formatCurrency(reportRow.totalValue) },
        ];

        return data;
    }

    const handleClickSearch = useCallback(async () => {
        filtersRef.current = {
            status,
            customerId,
            sellerId,
            dateInitial,
            dateEnd
        };
        reloadData();
        loadExportData(lastSortDirection, lastSortReference, showProducts);
    }, [
        status,
        customerId,
        sellerId,
        dateInitial,
        dateEnd,
        showProducts,
    ]);

    const clearSearch = () => {
        setCostumerId(0)
        setDateInitial('')
        setDateEnd('')
        setStatus('')
        setSellerId(0)
        setShowProducts(false);
    }

    async function handleClickExportExcel() {
        ReportExcelService.downloadSheet({
            filename: getFileNameWithDate({ filename: 'Orçamentos', extension: 'xlsx' }),
            headData,
            bodyData: exportBodyData,
            totalsData: exportTotals,
        });
    }

    return (
        <MainPageContentWrapper>
            <div className="row d-flex align-items-center">
                <div className="col-lg-6">
                    <ApiResourceSelect
                        label="Cliente"
                        getOptionLabel={(option: Customer) => `${option.id} - ${option.name}`}
                        value={customerId}
                        onSelect={(option) => setCostumerId(option?.id || 0)}
                        apiSearchHandler={(typedText) => CustomerService.getCustomersFiltered({ name: typedText, nameWithId: true, type: 'customer' })}
                        getSelectedOption={(loadedOptions) => {
                            if(!customerId) return null;
                            return loadedOptions.find((option) => option.id === Number(customerId)) ?? CustomerService.getCustomerById(customerId)
                        }}
                    />
                </div>

                <div className="col-lg-6">
                    <ApiResourceSelect
                        label="Vendedor"
                        getOptionLabel={(option: Seller) => option.name}
                        value={sellerId}
                        onSelect={(option) => setSellerId(option?.id || 0)}
                        apiSearchHandler={(typedText) => SellerService.getSellersFiltered({ name: typedText, type: 'seller' })}
                        getSelectedOption={(loadedOptions) => {
                            if(!sellerId) return null;
                            return loadedOptions.find((option) => option.id === Number(sellerId)) ?? CustomerService.getCustomerById(sellerId)
                        }}
                    />
                </div>

                <div className="col-lg-3">
                    <TextField
                        type="date"
                        label="Data"
                        margin="normal"
                        size="small"
                        value={dateInitial}
                        onChange={(e) => setDateInitial(e.target.value)}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    DE
                                </InputAdornment>
                            ),
                        }}
                        required
                    />
                </div>

                <div className="col-lg-3">
                    <TextField
                        type="date"
                        label="Data"
                        margin="normal"
                        size="small"
                        value={dateEnd}
                        onChange={(e) => setDateEnd(e.target.value)}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    ATÉ
                                </InputAdornment>
                            ),
                        }}
                        required
                    />
                </div>

                <div className="col-lg-6">
                    <TextField
                        select
                        label="Situação"
                        margin="normal"
                        size="small"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                    >
                        <MenuItem key="0" value="">Selecione</MenuItem>
                        <MenuItem key="1" value="launched">Lançado em Pedidos</MenuItem>
                        <MenuItem key="2" value="open">Aberto</MenuItem>
                    </TextField>
                </div>

                <div className="col-lg-4">
                    <FormControlLabel
                        value="end"
                        control={
                            <Checkbox
                                color="primary"
                                checked={showProducts}
                                onChange={(_, checked) => setShowProducts(checked)}
                            />
                        }
                        label="Exibir os produtos"
                        labelPlacement="end"
                        className="ml-1"
                    />
                </div>

                <div className="col-12 d-flex mt-6">
                    <Button
                        type="button"
                        variant="primary"
                        disabled={isSearching}
                        className="mx-2"
                        onClick={handleClickSearch}
                    >
                        {isSearching ? <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                            <span className='ml-2'>
                                Aguarde...
                            </span>
                        </> : <>
                            <span>
                                Pesquisar
                            </span>
                        </>}
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={clearSearch}
                    >
                        Limpar
                    </Button>
                </div>
            </div>

            <div className="row">
                <div className="col-12 d-flex align-items-center">
                    <PDFDownloadLink
                        document={(
                            <PdfReportDocument
                                title={"Relatório de Orçamentos"}
                                companyId={user.companyId}
                                bodyData={exportBodyData}
                                headData={headData}
                                totals={exportTotals}
                            />
                        )}
                        fileName={getFileNameWithDate({ filename: 'Orçamentos', extension: 'pdf' })}
                    >
                            <Button
                                variant="secondary"
                                className="ml-3"
                                type="button"
                            >
                                <i className="flaticon2-printer"></i>
                                Exportar PDF
                            </Button>
                    </PDFDownloadLink>
                        <Button
                            variant="secondary"
                            className="ml-3"
                            type="button"
                            onClick={handleClickExportExcel}
                        >
                            <i className="flaticon2-sheet"></i>
                            Exportar Excel
                        </Button>
                </div>
            </div>

            <div className="col-lg-12 mt-3">
                <ListWithModalChangeSituation
                    headData={headData}
                    bodyData={bodyData}
                    loadData={loadData}
                    sortable={true}
                    totalCount={countBudgetsPaginated}
                    triggerLoad={triggerLoad}
                    setTriggerLoad={setTriggerLoad}
                    lastCell={false}
                />
            </div>

            <div className="col-lg-12 mt-2">
                <div className="card w-50">
                    <div className="card-body row">
                        <ReportDetails 
                            bodyData={[
                                { text: 'Total de orçamentos', value: String(countBudgets) }
                            ]}
                        />
                    </div>
                </div>
            </div>
        </MainPageContentWrapper>
    );
}